import { Alert, flash, SubmitButton } from "@xola/ui-kit";
import { AxiosResponse } from "axios";
import { requiredString } from "common";
import { FormikProps } from "formik";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { Experience, ReviewDestination, ReviewPreference, UserPreferences } from "types";
import { object, string } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getStrippedHtml } from "../../../utils/getStrippedHtml";
import { OverridingExperiencesList } from "../../Common/OverridingExperiencesList";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { CustomReviewEmailTemplate } from "../CustomReviewEmailTemplate";
import { CustomReviewSmsTemplate } from "../CustomReviewSmsTemplate";
import { ReviewForm } from "../ReviewForm";

export const GlobalReviews = () => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const { data: experiences, isLoading } = useAutoFetchCollection<Experience>("experiences");
    const { gratuity, review } = seller.preferences;
    const reviewOverridingExperiences = experiences.filter((experience: Experience) => experience.reviewPreference);
    const computeInitialSendSMS = (review?: ReviewPreference) =>
        review?.destination === ReviewDestination.CUSTOM && !!review?.reviewLink && review.reviewLink !== "";
    const [sendSMS, setSendSMS] = useState(computeInitialSendSMS(review));
    const formikRef = useRef<FormikProps<ReviewPreference>>(null);

    const { mutateAsync: save } = useMutation(
        async (payload: ReviewPreference): Promise<AxiosResponse<ReviewPreference>> => {
            const url = `/sellers/${seller.id}/preferences/review`;
            return payload?.id ? api.put<ReviewPreference>(url, payload) : api.post<ReviewPreference>(url, payload);
        },
    );

    const onSave = async (values: any) => {
        const payload = { ...values };
        // Only include reviewTemplate if destination is CUSTOM
        if (values.destination !== ReviewDestination.CUSTOM) {
            delete payload.reviewTemplate;
        }
        // Only include reviewLink if destination is GOOGLE or CUSTOM
        if (values.destination !== ReviewDestination.CUSTOM && values.destination !== ReviewDestination.GOOGLE) {
            delete payload.reviewLink;
        } else if (values.destination === ReviewDestination.CUSTOM) {
            // For CUSTOM: clear reviewLink if sendSMS is false, otherwise trim it
            payload.reviewLink = sendSMS ? payload.reviewLink?.trim() : "";
        } else {
            // For GOOGLE: always trim reviewLink
            payload.reviewLink = payload.reviewLink?.trim();
        }

        const { data: result } = await save(payload);
        formikRef.current?.setValues(getInitialValues({ ...seller.preferences, review: result }));
        setSendSMS(computeInitialSendSMS(result));
        invalidate("sellers", seller.id);
        flash.success("Successfully updated global reviews");
    };

    const getExperience = () => {
        if (!gratuity.enabled || gratuity?.experiences?.all) {
            return experiences[0];
        }

        return experiences.find((experience) => gratuity?.experiences?.experiences?.includes(experience.id));
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                initialValues={getInitialValues(seller.preferences)}
                validationSchema={reviewPreferenceValidationSchema(sendSMS)}
                innerRef={formikRef}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={onSave}
            >
                {({ handleSubmit, handleReset, setValues }) => {
                    const handleResetClick = (handleReset: () => void) => () => {
                        handleReset();
                        setValues(getInitialValues(seller.preferences));
                        setSendSMS(computeInitialSendSMS(review));
                    };

                    return (
                        <>
                            <h2>Global Customer Reviews</h2>
                            <div className="mb-10 divide-y divide-gray-light">
                                <ContentLayout.Section
                                    title="Review Destination"
                                    description="Review emails are sent to customers one day after trip completion"
                                >
                                    <div className="space-y-4">
                                        <div className="w-full max-w-[560px]">
                                            {isLoading ? (
                                                <Loader size="tiny" className="p-2" text="Loading Experiences" />
                                            ) : (
                                                <div>
                                                    {reviewOverridingExperiences.length > 0 ? (
                                                        <OverridingExperiencesList
                                                            experiences={reviewOverridingExperiences}
                                                        />
                                                    ) : (
                                                        <NoOverrides />
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <ReviewForm />
                                    </div>
                                </ContentLayout.Section>

                                <CustomReviewEmailTemplate experience={getExperience()} />

                                <CustomReviewSmsTemplate
                                    experience={experiences[0]}
                                    sendSMS={sendSMS}
                                    setSendSMS={setSendSMS}
                                />
                            </div>

                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton onClick={handleSubmit}>Save</SubmitButton>

                                <SecondaryButton type="reset" onClick={handleResetClick(handleReset)}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const NoOverrides = () => {
    return (
        <Alert color="primary">
            <div className="flex flex-col space-y-2">
                <span className="font-bold">Overrides</span>
                <span className="text-primary-darker">All products use the review settings configured here</span>
            </div>
        </Alert>
    );
};

const getInitialValues = (userPreferences: UserPreferences) => {
    const initialValues = {
        destination: ReviewDestination.OFF,
        sendReviewEmailToAllTravelers: false,
        enableSmartReview: false,
        smartReviewThreshold: 4,
        ...userPreferences?.review,
    };

    if (initialValues.reviewTemplate) {
        initialValues.reviewTemplate = getStrippedHtml(initialValues.reviewTemplate) ?? "";
    }

    return initialValues;
};

export const reviewPreferenceValidationSchema = (sendSMS: boolean) => {
    return object().shape({
        reviewTemplate: string().when("destination", {
            is: ReviewDestination.CUSTOM,
            then: requiredString(),
        }),
        reviewLink: string().when("destination", {
            is: (destination: ReviewDestination) =>
                destination === ReviewDestination.GOOGLE || (destination === ReviewDestination.CUSTOM && sendSMS),
            then: string().trim().required("is required").url("must be a valid URL"),
            otherwise: string().trim(),
        }),
    });
};
